"use client"

import { useState, useEffect } from "react"
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Grid,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import axios from "axios"
import BaseURL from "../../api/AppURL";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "98%", sm: "95%", md: "85%", lg: "75%" },
  maxWidth: 1200,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  maxHeight: "95vh",
  overflowY: "auto",
}

export default function AddSaleModal({ open, onClose, onSaveSuccess }) {
  const [paymentMode, setPaymentMode] = useState("")
  const [customers, setCustomers] = useState([])
  const [products, setProducts] = useState([])
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [loadingCustomers, setLoadingCustomers] = useState(false)
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [formData, setFormData] = useState({
    mobile_no: "",
    address: "",
  })
  const [saleDate, setSaleDate] = useState("")
  const [deliveryDate, setDeliveryDate] = useState("")
  const [deliveryBy, setDeliveryBy] = useState("")
  const [productRows, setProductRows] = useState([])
  const [totalSum, setTotalSum] = useState(0)
  // Snackbar states
  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMsg, setAlertMsg] = useState("")
  const [alertType, setAlertType] = useState("error")
  const [storeClicked, setStoreClicked] = useState(false)

  useEffect(() => {
    if (open) {
      fetchData()
      // Optionally resetForm() on modal open
    }
    // eslint-disable-next-line
  }, [open])

  useEffect(() => {
    // Update total sum when productRows change
    const sum = productRows.reduce((acc, row) => acc + Number(row.totalPrice), 0)
    setTotalSum(sum)
  }, [productRows])

  const resetForm = () => {
    setSelectedCustomer(null)
    setSelectedProduct(null)
    setFormData({ mobile_no: "", address: "" })
    setSaleDate("")
    setDeliveryDate("")
    setDeliveryBy("")
    setProductRows([])
    setTotalSum(0)
    setAlertMsg("")
    setStoreClicked(false)
    setPaymentMode("")
  }

  const fetchData = async () => {
    setLoadingCustomers(true)
    setLoadingProducts(true)
    try {
      const token = localStorage.getItem("auth_token")
      const response = await axios.get(`${BaseURL}/all/sale`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
      setCustomers(response.data.customers || [])
      setProducts(response.data.products || [])
    } catch (err) {
      console.error("Error fetching data:", err)
    } finally {
      setLoadingCustomers(false)
      setLoadingProducts(false)
    }
  }

  const handleCustomerChange = (event, newValue) => {
    setSelectedCustomer(newValue)
    if (newValue) {
      setFormData({
        mobile_no: newValue.mobile_no || "",
        address: newValue.address || "",
      })
    } else {
      setFormData({
        mobile_no: "",
        address: "",
      })
    }
  }

  const handleProductChange = async (event, newValue) => {
    // setSelectedProduct(null) 
     setSelectedProduct(newValue)

    if (newValue) {
      try {
        const token = localStorage.getItem("auth_token")
        const response = await axios.get(`${BaseURL}/get/avaq/${newValue.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        })

        const { ava_quantity, trade_price } = response.data

        // নতুন enriched প্রোডাক্ট বানাও API এর ডেটা দিয়ে
        const enrichedProduct = {
          ...newValue,
          trade_price: trade_price,
          available_quantity: ava_quantity,
        }

        setSelectedProduct(enrichedProduct)
      } catch (err) {
        console.error("Failed to fetch available quantity:", err)
      }
    }
  }

  // Snackbar alert trigger function
  const showAlert = (msg, type = "error") => {
    setAlertMsg(msg)
    setAlertType(type)
    setAlertOpen(true)
  }

  const handleModalClose = () => {
    onClose()
    // resetForm() 
  }

  // ADD MORE validation and add logic
  const handleAddMore = () => {
    // Validate required fields
    if (
      !selectedCustomer ||
      !selectedProduct ||
      !saleDate ||
      !deliveryDate ||
      !paymentMode
    ) {
      showAlert("Please fill all required fields.", "error")
      return
    }
    // Check if product already added
    if (productRows.some(row => row.product.id === selectedProduct.id)) {
      showAlert("Product already added", "error")
      return
    }
    // Get availableQuantity (demo: random or from API)
    const availableQuantity = selectedProduct.available_quantity || 0
    const tradePrice = selectedProduct.trade_price || "0.00"
    const displayText = `${selectedProduct.name} ${selectedProduct.pack_size || ""} ${selectedProduct.product_unit?.name || ""}`.trim()
    // Add new row
    setProductRows([
      ...productRows,
      {
        product: selectedProduct,
        displayText,
        availableQuantity,
        quantity: 0,
        sample: 0,
        bonus: 0,
        tradePrice,
        totalPrice: 0,
      },
    ])
    setSelectedProduct(null)
  }

  // Remove row
  const handleRemoveRow = index => {
    const newRows = [...productRows]
    newRows.splice(index, 1)
    setProductRows(newRows)
  }

  // Handle changes in row fields
  const handleRowChange = (index, field, value) => {
    let updatedRows = [...productRows]
    let row = { ...updatedRows[index] }
    // Only allow digits
    value = value.replace(/[^0-9]/g, "")
    row[field] = value
    // Quantity change: update total price
    if (field === "quantity") {
      const qty = Number(value)
      const price = Number(row.tradePrice)
      row.totalPrice = qty * price
    }
    updatedRows[index] = row
    setProductRows(updatedRows)
  }

  // Sale Store validation & submit
  const handleSaleStore = async () => {
    setStoreClicked(true)

    if (productRows.length === 0) {
      showAlert("Please click the ADD MORE button before submitting the form.", "error")
      return
    }

    try {
      const token = localStorage.getItem("auth_token")

      const payload = {
        customer_id: selectedCustomer?.id,
        date: saleDate,
        delivery_date: deliveryDate,
        payment_mode: paymentMode,
        delivery_by: deliveryBy,
        product_id: productRows.map(row => row.product.id),
        buying_qty: productRows.map(row => Number(row.quantity) || 0),
        unit_price: productRows.map(row => Number(row.tradePrice) || 0),
        sample: productRows.map(row => Number(row.sample) || 0),
        bonus: productRows.map(row => Number(row.bonus) || 0),
      }

      const response = await axios.post(
        `${BaseURL}/store/sale`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      )

      // Success: show parent snackbar, refresh table
      if (onSaveSuccess) {
        onSaveSuccess("Sale Saved successfully!")
      }
      resetForm()
      onClose()
    } catch (error) {
      console.error("Failed to save sale:", error)
      const errorMsg = error.response?.data?.message || "Something went wrong while saving."
      showAlert(errorMsg, "error")
    }
  }


  return (
    <>
      <Modal open={open} onClose={handleModalClose}>
        <Box sx={style}>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5" fontWeight={600}>
              Add Sale
            </Typography>
            <IconButton onClick={handleModalClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Body */}
          <Grid
            container
            spacing={4}
            sx={{
              flexWrap: {
                xs: "wrap",
                md: "nowrap",
              },
              "@media (max-width:1265px) and (min-width:1200px)": {
                flexWrap: "nowrap",
              },
            }}
          >
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={customers}
                getOptionLabel={(option) => option.name || ""}
                value={selectedCustomer}
                onChange={handleCustomerChange}
                loading={loadingCustomers}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select MPO"
                    sx={{ mb: 2 }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={loadingCustomers ? "Loading customers..." : "No customers found"}
              />
              <TextField
                fullWidth
                label="Mobile Number"
                value={formData.mobile_no}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />
              <Autocomplete
                options={products}
                getOptionLabel={(option) => {
                  const name = option.name || ""
                  const packSize = option.pack_size || ""
                  const unit = option.product_unit?.name || ""
                  return `${name} ${packSize} ${unit}`.trim()
                }}
                value={selectedProduct}
                onChange={handleProductChange}
                loading={loadingProducts}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Product"
                    sx={{ mb: 2 }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={loadingProducts ? "Loading products..." : "No products found"}
              />
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sale Date"
                type="date"
                value={saleDate}
                onChange={e => setSaleDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Delivery Date"
                type="date"
                value={deliveryDate}
                onChange={e => setDeliveryDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ mb: 1 }}>Payment Mode</Typography>
                <RadioGroup
                  row
                  value={paymentMode}
                  onChange={e => setPaymentMode(e.target.value)}
                >
                  <FormControlLabel value="cash" control={<Radio />} label="Cash" />
                  <FormControlLabel value="card" control={<Radio />} label="Card" />
                  <FormControlLabel value="check" control={<Radio />} label="Check" />
                </RadioGroup>
              </Box>
              <TextField
                fullWidth
                label="Delivery By"
                value={deliveryBy}
                onChange={e => setDeliveryBy(e.target.value)}
                sx={{ mb: 2 }}
              />

              {/* ADD MORE Button - Now in the right column */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  my: 2,
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{
                    bgcolor: "#61676f",
                    color: "white",
                    px: 2,
                    py: 0.5,
                    fontWeight: "bold",
                    fontSize: 17,
                    borderRadius: 1,
                    boxShadow: 2,
                    "&:hover": { bgcolor: "#42474e" },
                  }}
                  onClick={handleAddMore}
                >
                  ADD MORE
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Product Row Table */}
          {productRows.length > 0 && (
            <Box
              sx={{
                mt: 3,
                borderRadius: 2,
                boxShadow: 1,
                bgcolor: "#f8f8f8",
                p: 2,
                overflowX: "auto",
              }}
            >
              {productRows.map((row, idx) => (
                <Box
                  key={row.product.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "#f8f8f8",
                    p: 1,
                    mb: 2,
                    borderRadius: 2,
                    boxShadow: 1,
                    flexWrap: "nowrap", // prevent row wrap!
                    minWidth: 700, // force horizontal scroll if needed
                  }}
                >
                  {/* Product Name */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mx: 1,
                      minWidth: 120,
                      maxWidth: 150, // restrict max width
                      flexBasis: 120,
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        width: "100%",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        textAlign: "center",
                      }}
                    >
                      {row.displayText}
                    </Typography>
                  </Box>
                  {/* Available Quantity */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 1 }}>
                    <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Available Quantity
                    </Typography>
                    <TextField
                      value={row.availableQuantity}
                      InputProps={{
                        readOnly: true,
                        sx: { bgcolor: "#eaeaea", fontWeight: 600, borderRadius: 1 },
                      }}
                      size="small"
                      sx={{ width: 120 }}
                    />
                  </Box>
                  {/* Quantity */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 1 }}>
                    <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Quantity
                    </Typography>
                    <TextField
                      type="number"
                      value={row.quantity}
                      onChange={e => handleRowChange(idx, "quantity", e.target.value)}
                      size="small"
                      inputProps={{ min: 0 }}
                      sx={{ width: 120 }}
                    />
                  </Box>
                  {/* Sample */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 1 }}>
                    <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Sample
                    </Typography>
                    <TextField
                      type="number"
                      value={row.sample}
                      onChange={e => handleRowChange(idx, "sample", e.target.value)}
                      size="small"
                      inputProps={{ min: 0 }}
                      sx={{ width: 120 }}
                    />
                  </Box>
                  {/* Bonus */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 1 }}>
                    <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Bonus
                    </Typography>
                    <TextField
                      type="number"
                      value={row.bonus}
                      onChange={e => handleRowChange(idx, "bonus", e.target.value)}
                      size="small"
                      inputProps={{ min: 0 }}
                      sx={{ width: 120 }}
                    />
                  </Box>
                  {/* Trade Price */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 1 }}>
                    <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Trade Price
                    </Typography>
                    <TextField
                      value={row.tradePrice}
                      InputProps={{
                        readOnly: true,
                        sx: { bgcolor: "#eaeaea", fontWeight: 600, borderRadius: 1 },
                      }}
                      size="small"
                      sx={{ width: 120 }}
                    />
                  </Box>
                  {/* Total Price */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 1 }}>
                    <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Total Price
                    </Typography>
                    <TextField
                      value={row.totalPrice}
                      InputProps={{
                        readOnly: true,
                        sx: { bgcolor: "#eaeaea", fontWeight: 600, borderRadius: 1 },
                      }}
                      size="small"
                      sx={{ width: 120 }}
                    />
                  </Box>
                  {/* Remove Button */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ mb: 0.5, fontWeight: 600, color: "transparent" }}
                    >
                      cross
                    </Typography>
                    <IconButton
                      sx={{
                        bgcolor: "#e53935",
                        color: "white",
                        "&:hover": { bgcolor: "#c62828" },
                        borderRadius: 1,
                        width: 28,
                        height: 28,
                      }}
                      onClick={() => handleRemoveRow(idx)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
              {/* Total Price Box */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <TextField
                  value={totalSum}
                  label="Total Price"
                  InputProps={{
                    readOnly: true,
                    sx: { bgcolor: "#eaeaea", fontWeight: 600, borderRadius: 1 },
                    endAdornment: <InputAdornment position="end">৳</InputAdornment>,
                  }}
                  size="medium"
                  sx={{ width: 120, mr: { xs: 0, md: 2 } }}
                />
              </Box>
            </Box>
          )}

          {/* Footer */}
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              sx={{
                bgcolor: "#04b6e0",
                color: "white",
                px: 2,
                py: 0.5,
                fontSize: 18,
                fontWeight: 550,
                borderRadius: 1,
                "&:hover": { bgcolor: "#0299be" },
              }}
              onClick={handleSaleStore}
            >
              Sale Store
            </Button>
          </Box>
        </Box>
      </Modal>
      {/* Snackbar Alert */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={3000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setAlertOpen(false)}
          severity={alertType}
          elevation={6}
          variant="filled"
          sx={{ fontWeight: 600, fontSize: 16 }}
        >
          {alertMsg}
        </MuiAlert>
      </Snackbar>
    </>
  )
}